import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationInbox from './NotificationInbox';
import { DeepechoWordmark } from './DeepechoLogos';
interface MenuItem {
  icon: string;
  title: string;
  link: string;
}

interface MobileMenuProps {
  mainMenuItems: MenuItem[];
  isOpen: boolean;
  onClose: () => void;
  handleLogout: () => void;
  username: string;
  userInitials: string;
  go_profile: () => void;
  userRole?: string;
  isAdmin?: boolean;
  onReportProblem: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  mainMenuItems,
  isOpen,
  onClose,
  handleLogout,
  username,
  userInitials,
  go_profile,
  userRole,
  isAdmin,
  onReportProblem,
}) => {
  const { t } = useTranslation('Menu');
  const { t: tReport } = useTranslation('ProblemReport');
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const NavLink = ({ item }: { item: MenuItem }) => {
    const isActive = location.pathname.startsWith(item.link);
    return (
      <Link to={item.link} onClick={onClose}>
        <div
          className={`relative flex items-center gap-4 h-11 px-4 rounded-lg transition-colors ${
            isActive
              ? 'bg-primary-dui/10 text-primary-dui font-semibold'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <i className={`${item.icon} text-xl`}></i>
          <span className="text-sm">{item.title}</span>
          {isActive && (
            <div className="absolute right-0 h-6 w-1 bg-primary-dui rounded-full"></div>
          )}
        </div>
      </Link>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-30 transition-opacity md:hidden ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        ref={menuRef}
        className={`fixed top-0 left-0 z-40 h-full w-72 bg-card-color-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-xl transition-transform duration-300 ease-in-out md:hidden p-4 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-8">
            <div className="text-center text-[#2B6CB0] dark:text-white">
              <DeepechoWordmark
                className="mx-auto h-5 w-auto max-w-[160px]"
                title={t('logoAlt')}
              />
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 p-2"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <nav className="flex-1 space-y-2 overflow-visible">
            {mainMenuItems.map((item, index) => (
              <NavLink key={index} item={item} />
            ))}
            <NotificationInbox variant="sidebar" menuOpen />
          </nav>

          <div>
            {/* Profile Dropdown */}
            <div className="relative mb-2" ref={dropdownRef}>
              <div
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 font-bold text-sm flex-shrink-0">
                  {userInitials}
                </div>
                <div className="flex-1 overflow-hidden flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                    {username}
                  </p>
                  <i className={`ri-arrow-${profileDropdownOpen ? 'up' : 'down'}-s-line text-sm text-gray-500 dark:text-gray-400`}></i>
                </div>
              </div>

              {/* Dropdown Menu */}
              {profileDropdownOpen && (
                <div className="mt-1 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1">
                  <div
                    onClick={() => {
                      go_profile();
                      setProfileDropdownOpen(false);
                      onClose();
                    }}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                  >
                    <i className="ri-user-line text-base"></i>
                    <span>{t('Go to Profile')}</span>
                  </div>
                  {isAdmin && (
                    <Link to="/organization">
                      <div
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          onClose();
                        }}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                      >
                        <i className="ri-building-line text-base"></i>
                        <span>{t('Organization Settings')}</span>
                      </div>
                    </Link>
                  )}
                  <div
                    onClick={() => {
                      handleLogout();
                      setProfileDropdownOpen(false);
                      onClose();
                    }}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line text-base"></i>
                    <span>{t('Logout')}</span>
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                onClose();
                onReportProblem();
              }}
              className="relative flex w-full items-center gap-4 h-11 px-4 rounded-lg cursor-pointer transition-colors text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <i className="ri-feedback-line text-xl"></i>
              <span className="text-sm whitespace-nowrap">{tReport('menuEntry')}</span>
            </button>

            {/* About Link */}
            <Link to="/about" onClick={onClose}>
              <div className="relative flex items-center gap-4 h-11 px-4 rounded-lg cursor-pointer transition-colors text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                <i className="ri-information-line text-xl"></i>
                <div className="flex items-center justify-between flex-1">
                  <span className="text-sm whitespace-nowrap">{t('About')}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">v1.1</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
